import React, {
  useState,
  useCallback,
  memo,
  useMemo,
  ChangeEvent,
} from 'react';
import * as Styled from './styled';
import { Table } from '@leasequery/lq.react-component-library';
import {
  TableColumn,
  TableConfig,
  SelectOption,
} from '@leasequery/lq.react-component-library/dist/models';
import { buildLastStickyCell } from './helpers';
import { findLastIndex } from '../../util';
import TableHeader from './table-header/TableHeader';
import TableFooter from './table-footer/TableFooter';
import { ReportLabels } from '.';
import { ArchivedReport, ReportResult } from '../../models';

interface DataTableProps {
  data: ArchivedReport[] | ReportResult[] | Record<string, unknown>[];
  columns: TableColumn[];
  config?: TableConfig;
  labels?: ReportLabels;
  isSinglePageView?: boolean;
}

const ITEM_PER_PAGE_OPTIONS: SelectOption<number>[] = [
  { key: 10, label: '10', value: 10 },
  { key: 25, label: '25', value: 25 },
  { key: 50, label: '50', value: 50 },
  { key: 100, label: '100', value: 100 },
];

const prepareColumns = (columns: TableColumn[]): TableColumn[] => {
  const nextColumns = [...columns];
  // style last sticky column with RightShadowBorder
  const lastStickyColumnIndex = findLastIndex(columns, (col) => col.sticky);
  if (lastStickyColumnIndex >= 0) {
    const stickyCol = nextColumns[lastStickyColumnIndex];
    nextColumns[lastStickyColumnIndex] = {
      ...stickyCol,
      Cell: buildLastStickyCell(stickyCol.Cell as React.ComponentType),
    };
  }
  return nextColumns;
};

function DataTable({
  data,
  columns,
  config,
  labels,
  isSinglePageView,
}: DataTableProps): JSX.Element {
  const [rowCount, setRowCount] = useState(data.length);
  const [pageSize, setPageSize] = useState(ITEM_PER_PAGE_OPTIONS[0]);
  const [pageIndex, setPageIndex] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');

  const preparedColumns = useMemo(() => prepareColumns(columns), [columns]);
  const globalFilterChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setGlobalFilter(event.target.value);
    },
    [],
  );
  const pageinatorClickHandler = useCallback((page: number): void => {
    setPageIndex(page);
  }, []);
  const pageSizeChangeHandler = useCallback(
    (option: SelectOption<number>): void => {
      setPageSize(option);
      setPageIndex(0);
    },
    [],
  );
  const rowsCountChangeHandler = useCallback((count: number): void => {
    setRowCount(count);
    setPageIndex(0);
  }, []);

  return (
    <Styled.DataTable>
      <TableHeader
        itemCount={rowCount}
        pageSize={pageSize.value}
        pageIndex={pageIndex}
        globalFilter={globalFilter}
        labels={labels}
        onPageSizeChange={pageSizeChangeHandler}
        onPageinatorClick={pageinatorClickHandler}
        onGlobalFilterChange={globalFilterChangeHandler}
        singlePageView={isSinglePageView}
      />
      <Table
        columns={preparedColumns}
        data={data}
        config={config}
        pageSize={isSinglePageView ? data.length : pageSize.value}
        pageIndex={pageIndex}
        globalFilter={globalFilter}
        onRowsCountChange={rowsCountChangeHandler}
      />
      <TableFooter
        itemCount={rowCount}
        pageSize={pageSize.value}
        pageIndex={pageIndex}
        labels={labels}
        onPageSizeChange={pageSizeChangeHandler}
        onPageinatorClick={pageinatorClickHandler}
        singlePageView={isSinglePageView}
      />
    </Styled.DataTable>
  );
}

export default memo(DataTable);
